import { Photo } from "../Contract/Photos";

const CACHE_NAME = "homehub-photos-v1";

/**
 * Photo caching service that stores images locally to avoid re-downloading
 */
export class PhotoCacheService {
  private blobUrlCache: Map<string, string> = new Map();
  private cacheDisabledDueToCors = false;

  /**
   * Get a cached photo URL or fetch and cache it
   */
  async getCachedPhotoUrl(photo: Photo): Promise<string> {
    if (this.cacheDisabledDueToCors) {
      return photo.url;
    }

    // Check if we already have a blob URL in memory
    if (this.blobUrlCache.has(photo.id)) {
      return this.blobUrlCache.get(photo.id)!;
    }

    try {
      // Try to get from Cache API
      const cache = await caches.open(CACHE_NAME);
      const cachedResponse = await cache.match(photo.id);

      if (cachedResponse) {
        const blob = await cachedResponse.blob();
        const blobUrl = URL.createObjectURL(blob);
        this.blobUrlCache.set(photo.id, blobUrl);
        return blobUrl;
      }

      // Not in cache, fetch from presigned URL
      console.log(`Caching photo: ${photo.id}`);
      const response = await fetch(photo.url);

      if (!response.ok) {
        throw new Error(`Failed to fetch photo: ${response.status}`);
      }

      // Clone the response so we can cache it and use it
      const responseClone = response.clone();

      // Cache the response
      await cache.put(photo.id, responseClone);

      // Create blob URL for immediate use
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      this.blobUrlCache.set(photo.id, blobUrl);

      return blobUrl;
    } catch (e) {
      // If S3 CORS is not configured for browser fetch, disable JS-level caching
      // for this session and use signed URLs directly.
      if (e instanceof TypeError) {
        this.cacheDisabledDueToCors = true;
        console.warn(
          "Photo cache disabled for this session due to CORS. Falling back to signed URLs.",
        );
      } else {
        console.error(
          "Failed to cache photo, falling back to original URL:",
          e,
        );
      }
      // Fallback to original URL if caching fails
      return photo.url;
    }
  }

  /**
   * Preload all photos into cache
   */
  async preloadPhotos(photos: Photo[]): Promise<void> {
    const promises = photos.map((photo) => this.getCachedPhotoUrl(photo));
    await Promise.all(promises);
  }

  /**
   * Clear the photo cache
   */
  async clearCache(): Promise<void> {
    // Revoke all blob URLs
    this.blobUrlCache.forEach((url) => URL.revokeObjectURL(url));
    this.blobUrlCache.clear();

    // Clear the cache storage
    await caches.delete(CACHE_NAME);
    this.cacheDisabledDueToCors = false;
  }

  /**
   * Get cache size info
   */
  async getCacheInfo(): Promise<{ count: number; size?: number }> {
    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();

      // Try to estimate size if storage API is available
      if ("storage" in navigator && "estimate" in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        return {
          count: keys.length,
          size: estimate.usage,
        };
      }

      return { count: keys.length };
    } catch {
      return { count: 0 };
    }
  }
}

// Export singleton instance
export const photoCacheService = new PhotoCacheService();
